import { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../admin/api';
import GrantExpiryChip from '../../admin/components/GrantExpiryChip';
import {
  AdminConfirmationDialog,
  AdminDataTable,
} from '../../sections/admin/shared';
import { AdminRouteLayout, RequestState, useAsyncState } from './AdminRoutePageShared';

export default function AccessControlGrantsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const grants = useAsyncState([]);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    const load = async () => {
      grants.setLoading();
      try {
        const response = await fetchAdmin('/access/grants');
        grants.setData(response.grants || []);
      } catch (error) {
        grants.setError(toErrorMessage(error));
      }
    };

    load();
  }, []);

  const completeAction = async () => {
    try {
      await postAdmin(pendingAction.path);
      enqueueSnackbar(pendingAction.success, { variant: 'success' });
      const refreshed = await fetchAdmin('/access/grants');
      grants.setData(refreshed.grants || []);
    } catch (error) {
      enqueueSnackbar(toErrorMessage(error), { variant: 'error' });
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <AdminRouteLayout title="Grants" breadcrumbTitle="Access Control">
      <RequestState loading={grants.state.loading} error={grants.state.error}>
        <AdminDataTable
          title="Elevated Grants"
          description="Currently issued elevated grants and their expiry windows."
          columns={[
            { field: 'staffId', headerName: 'Staff ID' },
            { field: 'permission', headerName: 'Permission' },
            { field: 'status', headerName: 'Status' },
            {
              field: 'expires',
              headerName: 'Expiry',
              render: (row) => <GrantExpiryChip expiresAt={row.expiresAt} />,
            },
            {
              field: 'actions',
              headerName: 'Actions',
              render: (row) => (
                <Button size="small" color="error" onClick={() => setPendingAction({ path: `/access/grants/${row.id}/revoke`, success: 'Grant revoked' })}>
                  Revoke
                </Button>
              ),
            },
          ]}
          rows={(grants.state.data || []).map((row) => ({
            id: row.id,
            staffId: row.staff_id,
            permission: row.permission_code,
            status: row.status,
            expiresAt: row.expires_at,
          }))}
          emptyTitle="No grants"
          emptyDescription="The grants endpoint returned no elevated grant records."
        />
      </RequestState>
      <AdminConfirmationDialog
        open={Boolean(pendingAction)}
        title="Confirm action"
        description="This action will be sent to the governed backend immediately."
        confirmLabel="Continue"
        onClose={() => setPendingAction(null)}
        onConfirm={completeAction}
      />
    </AdminRouteLayout>
  );
}
